// ============================================================================
// Utility Functions for DDARP Enterprise App
// ============================================================================

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// Merge Tailwind classes
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Format latency value
export function formatLatency(ms: number): string {
  if (ms < 0) return 'N/A';
  if (ms < 1) return '<1ms';
  return `${Math.round(ms)}ms`;
}

// Get latency quality
export function getLatencyQuality(ms: number): 'excellent' | 'good' | 'fair' | 'poor' {
  if (ms < 50) return 'excellent';
  if (ms < 100) return 'good';
  if (ms < 150) return 'fair';
  return 'poor';
}

// Get latency color class
export function getLatencyColor(ms: number): string {
  const quality = getLatencyQuality(ms);
  const colors = {
    excellent: 'text-latency-excellent',
    good: 'text-latency-good',
    fair: 'text-latency-fair',
    poor: 'text-latency-poor',
  };
  return colors[quality];
}

// Get latency background color
export function getLatencyBgColor(ms: number): string {
  const quality = getLatencyQuality(ms);
  const colors = {
    excellent: 'bg-latency-excellent',
    good: 'bg-latency-good',
    fair: 'bg-latency-fair',
    poor: 'bg-latency-poor',
  };
  return colors[quality];
}

// Get status color class
export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    active: 'text-node-active',
    warning: 'text-node-warning',
    offline: 'text-node-offline',
    unknown: 'text-node-unknown',
    established: 'text-bgp-established',
    idle: 'text-bgp-idle',
    down: 'text-bgp-down',
  };
  return colors[status.toLowerCase()] || 'text-gray-500';
}

// Format bytes to human readable
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

// Format duration in seconds to human readable
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  }
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  return `${days}d ${hours}h`;
}

// Format uptime from seconds
export function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);

  if (days > 0) {
    return `${days}d ${hours}h ${mins}m`;
  }
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
}

// Format date to relative time
export function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const target = new Date(date);
  const diffMs = now.getTime() - target.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return target.toLocaleDateString();
}

// Format date to ISO string (for display)
export function formatDateTime(date: Date | string): string {
  return new Date(date).toLocaleString();
}

// Calculate percentage
export function calcPercentage(value: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
}

// Truncate string
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

// Generate random ID
export function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

// Sleep utility for async operations
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Debounce function
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

// Throttle function
export function throttle<T extends (...args: unknown[]) => unknown>(
  fn: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Group array by key
export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce((result, item) => {
    const groupKey = String(item[key]);
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {} as Record<string, T[]>);
}

// Sort array by key
export function sortBy<T>(array: T[], key: keyof T, order: 'asc' | 'desc' = 'asc'): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });
}

// Check if WireGuard handshake is stale (> 2 minutes)
export function isHandshakeStale(lastHandshake: Date | null): boolean {
  if (!lastHandshake) return true;
  const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000);
  return new Date(lastHandshake) < twoMinutesAgo;
}

// Parse WireGuard handshake time string
export function parseHandshakeTime(handshakeStr: string): Date | null {
  if (!handshakeStr || handshakeStr === 'Never') return null;
  // Handle formats like "1 minute, 23 seconds ago"
  const now = new Date();
  const match = handshakeStr.match(/(\d+)\s*(second|minute|hour|day)/gi);
  if (!match) return null;

  let totalSeconds = 0;
  for (const m of match) {
    const [, num, unit] = m.match(/(\d+)\s*(second|minute|hour|day)/i) || [];
    const value = parseInt(num, 10);
    switch (unit?.toLowerCase()) {
      case 'second':
        totalSeconds += value;
        break;
      case 'minute':
        totalSeconds += value * 60;
        break;
      case 'hour':
        totalSeconds += value * 3600;
        break;
      case 'day':
        totalSeconds += value * 86400;
        break;
    }
  }

  return new Date(now.getTime() - totalSeconds * 1000);
}

// T3 quality score calculation
export function calculateT3Score(metrics: {
  latency: number;
  loss: number;
  jitter: number;
}): number {
  // Lower is better
  const latencyTrit = metrics.latency < 50 ? 0 : metrics.latency < 150 ? 1 : 2;
  const lossTrit = metrics.loss < 0.1 ? 0 : metrics.loss < 1 ? 1 : 2;
  const jitterTrit = metrics.jitter < 5 ? 0 : metrics.jitter < 20 ? 1 : 2;

  return latencyTrit * 27 + lossTrit * 9 + jitterTrit * 3;
}

// Mask IP address for security (hides last two octets)
// Example: 34.217.226.123 -> 34.217.•••.•••
export function maskIP(ip: string | null | undefined): string {
  if (!ip) return 'N/A';

  // Handle IPv4
  const ipv4Match = ip.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (ipv4Match) {
    return `${ipv4Match[1]}.${ipv4Match[2]}.•••.•••`;
  }

  // Handle IPv6 (mask last 4 groups)
  if (ip.includes(':')) {
    const parts = ip.split(':');
    if (parts.length >= 4) {
      return parts.slice(0, 4).join(':') + ':•••:•••:•••:•••';
    }
  }

  // Return original if format not recognized
  return ip;
}
